/**
 * 工具类
 *
 * @export
 * @class Tools
 */
const numberCH = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
const unitSmall= ["", "拾", "佰", "仟"];
const unitLarge = ["", "万", "亿"];
const unitTiny = ["角", "分"];
const unitBasic = "圆";
const unitFinal = "整";

const Tools = {

  /**
   * 对象深拷贝
   *
   * @param {*} obj
   * @returns objCopy
   */
  deepCopy: (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
  },

  /**
   * 清除obj中的undefined，null，"", NAN
   *
   * @param {*} obj
   * @returns 清除空值后的对象
   */
  clearObj: (obj: any) => {
    const o = {...obj};
    for (const key in o) {
      if (o.hasOwnProperty(key) && !o[key] && o[key] !== 0) {
        delete o[key];
      }
    }
    return o;
  },

  /**
   * 在数组指定位置插入元素
   *
   * @param {*} obj
   * @returns 清除空值后的对象
   */
  insertArray: (array: any[], index: number, item: any) => {
    array.splice(index, 0, item);
  },

  /**
   * 在数组指定位置删除元素
   *
   * @param {*} obj
   * @returns 清除空值后的对象
   */
  deleteArray: (array: any[], index: number) => {
    array.splice(index, 1);
  },

  /**
   * 将json拼接为请求参数字符串
   *
   * @param {*} params 参数对象
   * @returns 参数字符串
   */
  appendParams: (url: string, params: any) => {
    if (!url) {
      return "";
    }

    let str = "";
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        str += (key + "=" + params[key] + "&");
      }
    }
    if (str) {
      str = url + "?" + str.substr(0, str.length - 1);
    } else {
      str = url;
    }
    return str;
  },

  /**
   * 下载文件
   *
   * @param {*} url 文件地址
   */
  downloadFile: (url: string, fileName: string) => {
    const elemA = document.createElement("a");
    elemA.href = url;
    elemA.download = fileName || "文件下载";
    document.body.appendChild(elemA);
    elemA.click();
    elemA.onload = () => {
      document.body.removeChild(elemA);
    };
  },

  /**
   * 根据名称过滤下拉列表
   *
   * @param {string} name
   * @returns
   */
  filterOption: (input: string, option: any) => {
    return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
  },

  /**
   * 根据名称获取cookie
   *
   * @param {string} name
   * @returns
   */
  getCookie: (name: string) => {
    let arr;
    const reg = new RegExp("(^| )" + name + "=([^;]*)(;|$)");
    arr = document.cookie.match(reg);
    if (arr) {
      return unescape(arr[2]);
    }
    else {
     return null;
    }
  },

  /**
   * 设置cookie
   *
   * @param {string} name cookie名称
   * @param {string} value cookie值
   * @param {date} 过期时间
   * @returns
   */
  setCookie: (name: string, value: string, date: Date) => {
    if (Tools.getCookie(name)) {
      Tools.delCookie(name);
    }
    document.cookie = name + "=" + escape (value) + ";expires=" + date.toUTCString();
  },

  /**
   * 删除cookie
   *
   * @param {string} name cookie名称
   * @returns
   */
  delCookie: (name: string) => {
    const date = new Date();
    const value = Tools.getCookie(name);
    date.setTime(date.getTime() - 1);
    if (value != null) {
      document.cookie = name + "=" + value + ";expires=" + date.toUTCString();
    }
  },

  /**
   * 获取初始化分页对象
   *
   * @returns 初始化分页对象
   */
  getInitPagination: () => {
    return {
      pageSize: 25,
      current: 1,
      total: 0
    };
  },

  /**
   * 根据键值获取名称
   *
   * @param {any[]} list 列表
   * @param {string} keyValue 键值
   * @param {string | undefined} keyName 键名
   * @param {string | undefined} valueName 值名
   * @returns
   */
  mappingFilter: (list: any[], keyValue: (string | number), keyName?: string, valueName?: string) => {
    if (!list || !list.length) {
      return "";
    }
    const obj = list.find((item: any) => item[(keyName ? keyName : "dataKey")] === keyValue);
    if (obj) {
      return obj[(valueName ? valueName : "dataValue")];
    }
    else {
      return "";
    }
  },

  /**
   * 事件防抖动事件
   *
   * @param {F} fn type is Arrow Function
   * @param {number} delay
   * @returns
   */
  debounce: <F extends (...params: any[]) => void>(fn: F, delay: number): F => {
    let timeoutId: number;
    return function(this: any, ...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
  },

  /**
   * 获取对象数组的每个元素的指定属性值，组成数组返回
   * @param {array} array
   * @param {string} key
   * @returns {Array}
   */
  arrayPluck: (array: any[], key: string) => {
    const rs: any[] = [];
    array.forEach(obj => obj[key] && rs.push(obj[key]));
    return rs;
  },
  /**
   * 格式化时间
   * @param   time   时间
   * @param   bool   是否需要时分秒
   */
  formatTime: (time: number | string, bool: boolean) => {
    const dd = new Date(time);
    const y = dd.getFullYear();
    const m = dd.getMonth() + 1;
    const d = dd.getDate();
    const h = dd.getHours();
    const mm = dd.getMinutes();
    const s = dd.getSeconds();
    return `${y}-${m < 10 ? `0${m}` : m}-${d < 10 ? `0${d}` : d}${!bool ? "" : ` ${h < 10 ? `0${h}` : h}:${mm < 10 ? `0${mm}` : mm}:${s < 10 ? `0${s}` : s}`}`;
  },
  /**
   * 金额转中文（例如：12300000890）
   * @param   num    金额
   *
   * 1-  金额的长度取余数，比如，长度11除以4余3
   * 2-  先截取余数，把123存入数组
   * 3-  截取余数后，再每4位截取，最终的数组为["123", "0000", "0890"]
   * 4-  0000不处理
   * 5-  最后一位是0（比如 0340），要在最后加”零“
   * 6-  当前数字和下一个数字都为0，不匹配中文和单位，否则当前为0的只匹配中文
   * 7-  由于bool，最终会出现”零零“的情况，比如 ["130", "0022"]这种情况
   * 8-  去掉最后一个零
   * 9-  finalStr如果为空（金额只有角、分的情况），直接赋值”零”
   * 10- 没有角分，加上”整“
   * 11- 00不处理
   * 12- 角分只取前两位
   * 13- 处理只有角、分的情况（比如0.45、0.7）
   */
  transNumberToCH: (num: string | number) => {
    const arr = `${num}`.replace(/,/g, "").split(".");
    let leftNum = arr[0];
    let finalStr = "";
    let len = leftNum.length;
    const first = len % 4;  // 1
    const leftArr = [];
    if (first) { // 2
      leftArr.push(leftNum.substring(0, first));
      leftNum = leftNum.substring(first);
      len = leftNum.length;
    }
    for (let i = 0; i < len; i = i + 4) { // 3
      leftArr.push(leftNum.substring(i, i + 4));
    }
    const mLen = leftArr.length;
    leftArr.forEach((ele: any, i: number) => {
      if (+ele) { // 4
        const eleLen = ele.length;
        const bool = ele.substring(ele.length - 1) === "0"; // 5
        for (let j = 0; j < eleLen; j++) { // 6
          finalStr += !+ele[j] && !+ele[j + 1] ? "" : `${ numberCH[ele[j]] }${ !+ele[j] ? "" : unitSmall[(eleLen - (j + 1))] }`;
        }
        finalStr += unitLarge[mLen - (i + 1)];
        finalStr += ( bool ? "零" : "");
      }
    });
    finalStr = finalStr.replace(/零零/g, "零"); // 7
    if (finalStr.substring(finalStr.length - 1) === "零") { // 8
      finalStr = finalStr.substring(0, finalStr.length - 1);
    }
    finalStr = (finalStr || "零") + unitBasic; // 9
    if (!arr[1]) { // 10
      return finalStr += unitFinal;
    }
    const rightNum = arr[1];
    const rightLen = rightNum.length;
    let rightStr = "";
    if (+rightNum) { // 11
      for (let i = 0; i < rightLen; i++) {
        rightStr += `${numberCH[rightNum[i]]}${!+rightNum[i] ? "" : unitTiny[i]}`;
        if (i === 1) { // 12
          break;
        }
      }
    }
    finalStr += rightStr.replace(/角零/g, "角");
    if (finalStr.length > 2 && finalStr !== "零圆整" && finalStr.substr(0, 2) === "零圆") { // 13
      finalStr = finalStr.substr(2);
    }
    return finalStr;
  },
  /**
   * 金额转千分位格式，带两位小数
   * @param  num   金额
   */
  thousandSeparator: (num: string | number) => {
    if (isNaN(+num)) {
      return "";
    }
    const str: any = (+num).toFixed(2);
    const arr = str.split(".");
    return `${arr[0].replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,')}.${arr[1]}`;
  }

}

export default Tools;



// WEBPACK FOOTER //
// ./src/utils/tools.ts